import { FormatOptions } from "./format";
import { getActiveDateTimeFormat } from "./registry";

/**
 * Convenience formatters that read the active date/time preference from the
 * registry. Use these in pure (non-React) code; React components should prefer
 * the `useDateTimeFormat` hook so they re-render when the preference changes.
 *
 * All accept a `Date`, an epoch-millisecond number, or a date string / ISO
 * value (anything `new Date(...)` understands). Invalid inputs return the
 * `fallback` (default `""`) so a single bad value can't throw inside a table
 * cell or chart axis.
 */

export type DateInput = Date | string | number | null | undefined;

/** Coerce a {@link DateInput} into a valid `Date`, or `null`. */
export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatWith(
  value: DateInput,
  options: FormatOptions,
  fallback: string,
): string {
  const date = toDate(value);
  if (date === null) {
    return fallback;
  }
  return getActiveDateTimeFormat().format(date, options);
}

/** Date only, honoring the active preset (e.g. `30.06.2026`). */
export function formatDate(value: DateInput, fallback = ""): string {
  return formatWith(value, { time: "none" }, fallback);
}

/**
 * Below this age a timestamp reads as relative ("3 minutes ago"); at or past
 * it, as a date. Shared by every surface that shows a created/modified time.
 */
export const RECENT_THRESHOLD_MS = 24 * 60 * 60 * 1000;

const relativeFmt = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

/**
 * Locale-aware relative phrasing: "now", "3 minutes ago", "2 hours ago".
 *
 * Deliberately not run through the date preference: a date-ordering/clock
 * preset says nothing about relative wording, so it stays locale-driven.
 * `Intl.RelativeTimeFormat` localizes a (value, unit) pair without picking the
 * unit, so this buckets into now / minutes / hours only; callers switch to a
 * date once {@link isRecent} is false. Floored to minutes, matching the
 * 60-second tick relative displays re-render on.
 */
export function formatRelativeTime(value: DateInput, fallback = ""): string {
  const date = toDate(value);
  if (date === null) {
    return fallback;
  }
  const mins = Math.round((date.getTime() - Date.now()) / 60_000);
  if (mins > -1) {
    return relativeFmt.format(0, "second");
  }
  if (mins > -60) {
    return relativeFmt.format(mins, "minute");
  }
  return relativeFmt.format(Math.round(mins / 60), "hour");
}

/** Whether `value` is within {@link RECENT_THRESHOLD_MS} of now. */
export function isRecent(value: DateInput): boolean {
  const date = toDate(value);
  return date !== null && Date.now() - date.getTime() < RECENT_THRESHOLD_MS;
}

/** Date + `HH:mm`, honoring the active preset and clock. */
export function formatDateTime(value: DateInput, fallback = ""): string {
  return formatWith(value, { time: "minutes" }, fallback);
}

/** Date + `HH:mm:ss`. */
export function formatDateTimeWithSeconds(
  value: DateInput,
  fallback = "",
): string {
  return formatWith(value, { time: "seconds" }, fallback);
}

/**
 * Escape hatch for callers that need full control over the granularity and
 * timezone display in one call — including millisecond precision
 * (`{ time: "millis" }`), which has no named helper.
 */
export function formatDateTimeWithOptions(
  value: DateInput,
  options: FormatOptions,
  fallback = "",
): string {
  return formatWith(value, options, fallback);
}
